// Icon — the one glyph registry (inevitability P33 / CC2).
//
// The synthesis names this "the single change that buys the most
// inevitability in the app": before it, every glyph was a hand-drawn
// inline `<svg>` (40+ across 42 files), so a concept like "close" could
// not have ONE form — there was nowhere to house it, and I2-for-glyphs
// (one meaning ↔ one form) was unreachable by construction.
//
// This module is that home: a CLOSED named set (`IconName`), one weight
// (2px stroke on a 24-grid), `currentColor` so the glyph inherits text
// colour, and a token-driven scale. Every glyph is authored once here
// and imported by name; a new inline `<svg>` in a component is now a
// review smell, not a necessity. The dot/ellipsis collision (CC1) is
// resolved AT the registry: `overflow` ("…", as pen draws it) is the ONE row-
// overflow glyph, `dot` the ONE status dot — a component can no longer
// invent a fourth meaning for three dots because it reaches for a named
// glyph, not raw geometry.
//
// The geometry is vendored from Lucide (https://lucide.dev) v1.32.0 —
// one grid, one keyline, machine-consistent optics — behind this
// unchanged `IconName` facade, so every `<Icon name="…">` call site is
// untouched. The paths are inlined into `GLYPHS` below; `lucide-static`
// is a build-time devDependency only, so there is NO runtime dependency
// on Lucide. Lucide is ISC-licensed and requires its copyright notice be
// retained — see the adjacent `LICENSE` file (ISC + the Feather MIT
// ancestry). The one non-Lucide glyph is `dot` (a solid status mark
// Lucide has no outline equivalent for).
//
// Pure/props-driven. Decorative by default (`aria-hidden`), because an
// icon almost always sits beside its own label (a Button's text, an
// IconButton's required `label`). Pass `title` ONLY when the icon is the
// sole carrier of meaning — it then becomes an `img` with an accessible
// name. Never both a visible label and a `title` (that double-announces).

export type { AreaGlyphs, IconName, IconProps, IconSize };
export { Icon };

import type { ReactNode } from "react";
import "./Icon.css";
import { chatGlyphs } from "./glyphs/chat";
import { detailGlyphs } from "./glyphs/detail";
import { libraryGlyphs } from "./glyphs/library";
import { projectsGlyphs } from "./glyphs/projects";
import { studioGlyphs } from "./glyphs/studio";

// The closed set. Adding a shared glyph = one entry in `BASE_GLYPHS` + one
// name here; a glyph only one area uses goes in that area's file under
// `glyphs/` (one per lane, docs/implement/lanes.md), where its key is its
// name. Nothing else in the app authors SVG geometry. Grouped by role
// so the vocabulary reads as a system, not a grab-bag.
type BaseIconName =
    // structure / navigation
    | "chevron-right"
    | "chevron-down"
    | "chevron-left"
    | "chevron-up"
    | "chevrons-down" // minimise a panel (the chat panel's collapse)
    | "chevrons-up-down" // a switcher that opens a list (the institution)
    | "arrow-right"
    | "arrow-left" // back to where it came from (a breadcrumb)
    | "arrow-up-right" // open it where it lives
    | "arrow-up" // send (composer submit); move a part up
    | "arrow-down" // move a part down
    | "grip" // a drag handle (reorder a part)
    | "menu" // hamburger — reveal the collapsed rail
    | "expand" // diagonal arrows — grow a pane
    | "panel" // dock as a side panel
    | "panel-left" // show or hide the app's menu (the Studio toolbar)
    | "panel-left-close" // fold the menu into its icon rail
    | "panel-left-open" // unfold the menu (or keep the peek open)
    | "wifi-off" // the server (or the network) can't be reached
    | "refresh" // try again now
    | "home" // a project's landing surface (pinned tab)
    // actions
    | "close"
    | "check"
    | "plus"
    | "minus"
    | "search"
    | "external-link"
    | "trash" // destructive remove
    | "copy" // duplicate text to the clipboard
    | "pencil" // edit / author
    | "compose" // pen-on-paper — start a NEW chat (distinct from "+" = add file)
    | "gear" // settings
    | "sign-out" // leave the session
    | "undo"
    | "redo"
    | "paperclip" // attach
    | "download" // export to disk
    | "upload" // bring a file in
    | "command" // a runnable app command (prompt chevron)
    | "play" // start media
    | "help" // question-mark — a HelpHint affordance
    // domain content kinds + navigation objects (the rail's per-kind scent —
    // one form per kind, so a collapsed rail and every row read the kind at a
    // glance). Named by the CONCEPT they carry (source/material/export), so
    // the kind ↔ glyph bijection (I2) is enforced by the name, not convention.
    | "workspace" // the workspace switcher's place-of-rooms grid
    | "library" // the Bibliotek — a collection of sources/materials/packages
    | "folder" // a project
    | "canvas" // the Studio — cards laid out on a canvas
    | "knowledge" // the Knowledge bank — what the assistant knows
    | "module" // a module: parts put together
    | "course" // a course: modules put together
    | "pointer" // the select tool
    | "hand" // the pan tool
    | "scan" // fit a canvas to its content (not a curved arrow: that reads as undo)
    | "reorder" // change the order of a container's parts
    | "eye-off" // take off a view without deleting it (remove from the canvas)
    | "source" // a source file (raw input)
    | "sources" // a reply's references (Lucide `book-open`)
    | "file-up" // bring a source onto the canvas (the Studio toolbar)
    | "file-plus" // a new material on the canvas (the Studio toolbar)
    | "material" // an authored learning material
    | "export" // a produced export deliverable
    | "file" // an opaque asset file (image, audio, …), not yet a source
    | "document" // a text document (PDF, Word, plain text)
    | "presentation" // a slide deck
    | "unknown-file" // a file of a type the app can't show
    | "person" // the account identity avatar
    | "chat" // the Assistant / a conversation (speech bubble)
    | "sparkle" // AI generation
    | "sliders" // preferences
    | "database" // stored data
    | "clock" // time / recency
    | "anchor" // grounding — a claim tied to a source
    | "star" // rating / provenance highlight
    | "shield" // a verified / safe plan
    | "pin" // fasten a chat/scope to one resource
    | "archive" // set aside — the Library's archived scope
    | "rows-3" // the Compact list density
    | "rows-2" // the Detailed list density
    // appearance (the theme quick-toggle — one glyph per palette)
    | "sun" // light theme
    | "moon" // dark theme
    | "globe" // interface language (the language quick-picker)
    // editor content-block kinds (the Palette insert vocabulary)
    | "heading"
    | "paragraph"
    | "objective"
    | "mcq"
    | "image"
    | "video"
    | "audio"
    | "embed"
    // editor inline formatting marks
    | "bold"
    | "italic"
    | "code"
    | "link"
    | "unlink" // a broken chain — no source tie (ungrounded)
    // overflow + status (the CC1 dot resolution lives here)
    | "overflow" // "…" (horizontal ellipsis) — the ONE row-overflow glyph
    | "dot" // the ONE status dot
    | "info" // circle-i
    | "warning" // triangle-! — a caution to attend to
    | "danger" // octagon-! — a failure that blocks (the ONE danger form)
    | "error" // circle-x — a generic error mark
    | "stopped"
    // chat thread (pen "Chat – every entry")
    | "loading" // a step still running
    | "stop" // the reply was stopped
    | "alert" // not sent / went wrong
    | "conversation-long" // the conversation outgrew the assistant's memory
    | "budget"
    | "answered" // you answered the assistant's questions
    | "skipped" // you skipped them
    | "answer-line" // one question and its answer
    | "own-answer"; // write your own answer

type IconSize = "xs" | "sm" | "md" | "lg";

type IconProps = {
    readonly name: IconName;
    // Maps to the --icon-xs (12) / --icon-sm (16) / --icon-md (20) / --icon-lg
    // (24) tokens. Default sm — the app's dense default; a caller sizes up
    // deliberately. `xs` is for dense decorative inline glyphs (chip/tab/
    // provenance) and NEVER for a control's sole hit target; `lg` is for media-
    // frame and banner status glyphs.
    readonly size?: IconSize;
    // Present ONLY when the icon carries meaning with no adjacent text.
    // Absent → decorative (aria-hidden), the common case.
    readonly title?: string;
    readonly className?: string;
};

// Geometry vendored from Lucide v1.32.0, on Lucide's 24 grid. Every
// glyph inherits the shared svg attributes (stroke=currentColor, width
// 2, round caps/joins, fill=none) — one uniform weight, the consistency
// win. `overflow` is Lucide's `ellipsis` (three dots that read
// solid at 2px stroke); `dot` is the ONE status dot, a solid mark with
// no Lucide outline equivalent, so it fills instead and says so.
const BASE_GLYPHS: Record<BaseIconName, ReactNode> = {
    "chevron-right": <path d="m9 18 6-6-6-6" />,
    "chevron-down": <path d="m6 9 6 6 6-6" />,
    "chevron-left": <path d="m15 18-6-6 6-6" />,
    "chevron-up": <path d="m18 15-6-6-6 6" />,
    "chevrons-down": (
        <>
            <path d="m7 6 5 5 5-5" />
            <path d="m7 13 5 5 5-5" />
        </>
    ),
    "arrow-right": (
        <>
            <path d="M5 12h14" />
            <path d="m12 5 7 7-7 7" />
        </>
    ),
    "arrow-up": (
        <>
            <path d="m5 12 7-7 7 7" />
            <path d="M12 19V5" />
        </>
    ),
    "arrow-down": (
        <>
            <path d="M12 5v14" />
            <path d="m19 12-7 7-7-7" />
        </>
    ),
    grip: (
        <>
            <circle cx="9" cy="12" r="1" />
            <circle cx="9" cy="5" r="1" />
            <circle cx="9" cy="19" r="1" />
            <circle cx="15" cy="12" r="1" />
            <circle cx="15" cy="5" r="1" />
            <circle cx="15" cy="19" r="1" />
        </>
    ),
    menu: (
        <>
            <path d="M4 5h16" />
            <path d="M4 12h16" />
            <path d="M4 19h16" />
        </>
    ),
    expand: (
        <>
            <path d="M15 3h6v6" />
            <path d="m21 3-7 7" />
            <path d="m3 21 7-7" />
            <path d="M9 21H3v-6" />
        </>
    ),
    panel: (
        <>
            <rect width="18" height="18" x="3" y="3" rx="2" />
            <path d="M15 3v18" />
        </>
    ),
    "panel-left": (
        <>
            <rect width="18" height="18" x="3" y="3" rx="2" />
            <path d="M9 3v18" />
        </>
    ),
    "file-up": (
        <>
            <path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" />
            <path d="M14 2v5a1 1 0 0 0 1 1h5" />
            <path d="M12 12v6" />
            <path d="m15 15-3-3-3 3" />
        </>
    ),
    "file-plus": (
        <>
            <path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" />
            <path d="M14 2v5a1 1 0 0 0 1 1h5" />
            <path d="M9 15h6" />
            <path d="M12 18v-6" />
        </>
    ),
    "panel-left-close": (
        <>
            <rect width="18" height="18" x="3" y="3" rx="2" />
            <path d="M9 3v18" />
            <path d="m16 15-3-3 3-3" />
        </>
    ),
    refresh: (
        <>
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
            <path d="M21 3v5h-5" />
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
            <path d="M8 16H3v5" />
        </>
    ),
    "wifi-off": (
        <>
            <path d="M12 20h.01" />
            <path d="M8.5 16.429a5 5 0 0 1 7 0" />
            <path d="M5 12.859a10 10 0 0 1 5.17-2.69" />
            <path d="M19 12.859a10 10 0 0 0-2.007-1.523" />
            <path d="M2 8.82a15 15 0 0 1 4.177-2.643" />
            <path d="M22 8.82a15 15 0 0 0-11.288-3.764" />
            <path d="m2 2 20 20" />
        </>
    ),
    "panel-left-open": (
        <>
            <rect width="18" height="18" x="3" y="3" rx="2" />
            <path d="M9 3v18" />
            <path d="m14 9 3 3-3 3" />
        </>
    ),
    home: (
        <>
            <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" />
            <path d="M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        </>
    ),
    close: (
        <>
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
        </>
    ),
    check: <path d="M20 6 9 17l-5-5" />,
    plus: (
        <>
            <path d="M5 12h14" />
            <path d="M12 5v14" />
        </>
    ),
    minus: <path d="M5 12h14" />,
    search: (
        <>
            <path d="m21 21-4.34-4.34" />
            <circle cx="11" cy="11" r="8" />
        </>
    ),
    "external-link": (
        <>
            <path d="M15 3h6v6" />
            <path d="M10 14 21 3" />
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
        </>
    ),
    trash: (
        <>
            <path d="M10 11v6" />
            <path d="M14 11v6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
            <path d="M3 6h18" />
            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </>
    ),
    copy: (
        <>
            <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
            <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
        </>
    ),
    pencil: (
        <>
            <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
            <path d="m15 5 4 4" />
        </>
    ),
    compose: (
        <>
            <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z" />
        </>
    ),
    gear: (
        <>
            <path d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915" />
            <circle cx="12" cy="12" r="3" />
        </>
    ),
    undo: (
        <>
            <path d="M9 14 4 9l5-5" />
            <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11" />
        </>
    ),
    redo: (
        <>
            <path d="m15 14 5-5-5-5" />
            <path d="M20 9H9.5A5.5 5.5 0 0 0 4 14.5A5.5 5.5 0 0 0 9.5 20H13" />
        </>
    ),
    paperclip: (
        <path d="m16 6-8.414 8.586a2 2 0 0 0 2.829 2.829l8.414-8.586a4 4 0 1 0-5.657-5.657l-8.379 8.551a6 6 0 1 0 8.485 8.485l8.379-8.551" />
    ),
    download: (
        <>
            <path d="M12 15V3" />
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <path d="m7 10 5 5 5-5" />
        </>
    ),
    upload: (
        <>
            <path d="M12 3v12" />
            <path d="m17 8-5-5-5 5" />
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        </>
    ),
    command: (
        <>
            <path d="M12 19h8" />
            <path d="m4 17 6-6-6-6" />
        </>
    ),
    play: (
        <path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z" />
    ),
    help: (
        <>
            <circle cx="12" cy="12" r="10" />
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
            <path d="M12 17h.01" />
        </>
    ),
    workspace: (
        <>
            <rect width="7" height="7" x="3" y="3" rx="1" />
            <rect width="7" height="7" x="14" y="3" rx="1" />
            <rect width="7" height="7" x="14" y="14" rx="1" />
            <rect width="7" height="7" x="3" y="14" rx="1" />
        </>
    ),
    library: (
        <>
            <path d="m16 6 4 14" />
            <path d="M12 6v14" />
            <path d="M8 8v12" />
            <path d="M4 4v16" />
        </>
    ),
    canvas: (
        <>
            <rect width="7" height="9" x="3" y="3" rx="1" />
            <rect width="7" height="5" x="14" y="3" rx="1" />
            <rect width="7" height="9" x="14" y="12" rx="1" />
            <rect width="7" height="5" x="3" y="16" rx="1" />
        </>
    ),
    "arrow-left": (
        <>
            <path d="m12 19-7-7 7-7" />
            <path d="M19 12H5" />
        </>
    ),
    "arrow-up-right": (
        <>
            <path d="M7 7h10v10" />
            <path d="M7 17 17 7" />
        </>
    ),
    knowledge: (
        <>
            <path d="M12 18V5" />
            <path d="M15 13a4.17 4.17 0 0 1-3-4 4.17 4.17 0 0 1-3 4" />
            <path d="M17.598 6.5A3 3 0 1 0 12 5a3 3 0 1 0-5.598 1.5" />
            <path d="M17.997 5.125a4 4 0 0 1 2.526 5.77" />
            <path d="M18 18a4 4 0 0 0 2-7.464" />
            <path d="M19.967 17.483A4 4 0 1 1 12 18a4 4 0 1 1-7.967-.517" />
            <path d="M6 18a4 4 0 0 1-2-7.464" />
            <path d="M6.003 5.125a4 4 0 0 0-2.526 5.77" />
        </>
    ),
    course: (
        <>
            <path d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z" />
            <path d="M22 10v6" />
            <path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5" />
        </>
    ),
    module: (
        <>
            <path d="M2.97 12.92A2 2 0 0 0 2 14.63v3.24a2 2 0 0 0 .97 1.71l3 1.8a2 2 0 0 0 2.06 0L12 19v-5.5l-5-3-4.03 2.42Z" />
            <path d="m7 16.5-4.74-2.85" />
            <path d="m7 16.5 5-3" />
            <path d="M7 16.5v5.17" />
            <path d="M12 13.5V19l3.97 2.38a2 2 0 0 0 2.06 0l3-1.8a2 2 0 0 0 .97-1.71v-3.24a2 2 0 0 0-.97-1.71L17 10.5l-5 3Z" />
            <path d="m17 16.5-5-3" />
            <path d="m17 16.5 4.74-2.85" />
            <path d="M17 16.5v5.17" />
            <path d="M7.97 4.42A2 2 0 0 0 7 6.13v4.37l5 3 5-3V6.13a2 2 0 0 0-.97-1.71l-3-1.8a2 2 0 0 0-2.06 0l-3 1.8Z" />
            <path d="M12 8 7.26 5.15" />
            <path d="m12 8 4.74-2.85" />
            <path d="M12 13.5V8" />
        </>
    ),
    pointer: (
        <path d="M4.037 4.688a.495.495 0 0 1 .651-.651l16 6.5a.5.5 0 0 1-.063.947l-6.124 1.58a2 2 0 0 0-1.438 1.435l-1.579 6.126a.5.5 0 0 1-.947.063z" />
    ),
    hand: (
        <>
            <path d="M18 11V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2" />
            <path d="M14 10V4a2 2 0 0 0-2-2a2 2 0 0 0-2 2v2" />
            <path d="M10 10.5V6a2 2 0 0 0-2-2a2 2 0 0 0-2 2v8" />
            <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
        </>
    ),
    scan: (
        <>
            <path d="M3 7V5a2 2 0 0 1 2-2h2" />
            <path d="M17 3h2a2 2 0 0 1 2 2v2" />
            <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
            <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
        </>
    ),
    reorder: (
        <>
            <path d="m21 16-4 4-4-4" />
            <path d="M17 20V4" />
            <path d="m3 8 4-4 4 4" />
            <path d="M7 4v16" />
        </>
    ),
    "eye-off": (
        <>
            <path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49" />
            <path d="M14.084 14.158a3 3 0 0 1-4.242-4.242" />
            <path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143" />
            <path d="m2 2 20 20" />
        </>
    ),
    folder: (
        <path d="M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z" />
    ),
    sources: (
        <>
            <path d="M12 5v16" />
            <path d="M20.001 19A2 2 0 0022 17V5a2 2 0 00-1.999-2L16 3.002A5 5 0 0012 5a5 5 0 00-4-2H4a2 2 0 00-2 2v12a2 2 0 001.999 2H8a5 5 0 014 2 5 5 0 014-2z" />
        </>
    ),
    source: (
        <>
            <path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" />
            <path d="M14 2v5a1 1 0 0 0 1 1h5" />
        </>
    ),
    material: (
        <>
            <path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" />
            <path d="M14 2v5a1 1 0 0 0 1 1h5" />
            <path d="M10 9H8" />
            <path d="M16 13H8" />
            <path d="M16 17H8" />
        </>
    ),
    // An export is a PRODUCED artifact — a shippable box/cube (Lucide `package`).
    export: (
        <>
            <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z" />
            <path d="M12 22V12" />
            <polyline points="3.29 7 12 12 20.71 7" />
            <path d="m7.5 4.27 9 5.15" />
        </>
    ),
    // Lucide `file-image`: the source outline + a picture, so a file reads as
    // an asset next to a (text) source.
    file: (
        <>
            <path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" />
            <path d="M14 2v5a1 1 0 0 0 1 1h5" />
            <circle cx="10" cy="12" r="2" />
            <path d="m20 17-1.296-1.296a2.41 2.41 0 0 0-3.408 0L9 22" />
        </>
    ),
    document: (
        <>
            <path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" />
            <path d="M14 2v5a1 1 0 0 0 1 1h5" />
            <path d="M10 9H8" />
            <path d="M16 13H8" />
            <path d="M16 17H8" />
        </>
    ),
    presentation: (
        <>
            <path d="M2 3h20" />
            <path d="M21 3v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3" />
            <path d="m7 21 5-5 5 5" />
        </>
    ),
    "unknown-file": (
        <>
            <path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" />
            <path d="M12 17h.01" />
            <path d="M9.1 9a3 3 0 0 1 5.82 1c0 2-3 3-3 3" />
        </>
    ),
    person: (
        <>
            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
        </>
    ),
    chat: (
        <path d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719" />
    ),
    sparkle: (
        <>
            <path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z" />
            <path d="M20 2v4" />
            <path d="M22 4h-4" />
            <circle cx="4" cy="20" r="2" />
        </>
    ),
    sliders: (
        <>
            <path d="M10 5H3" />
            <path d="M12 19H3" />
            <path d="M14 3v4" />
            <path d="M16 17v4" />
            <path d="M21 12h-9" />
            <path d="M21 19h-5" />
            <path d="M21 5h-7" />
            <path d="M8 10v4" />
            <path d="M8 12H3" />
        </>
    ),
    database: (
        <>
            <ellipse cx="12" cy="5" rx="9" ry="3" />
            <path d="M3 5V19A9 3 0 0 0 21 19V5" />
            <path d="M3 12A9 3 0 0 0 21 12" />
        </>
    ),
    clock: (
        <>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
        </>
    ),
    anchor: (
        <>
            <path d="M12 6v16" />
            <path d="m19 13 2-1a9 9 0 0 1-18 0l2 1" />
            <path d="M9 11h6" />
            <circle cx="12" cy="4" r="2" />
        </>
    ),
    star: (
        <path d="M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z" />
    ),
    shield: (
        <>
            <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
            <path d="m9 12 2 2 4-4" />
        </>
    ),
    pin: (
        <>
            <path d="M12 17v5" />
            <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
        </>
    ),
    archive: (
        <>
            <rect width="20" height="5" x="2" y="3" rx="1" />
            <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
            <path d="M10 12h4" />
        </>
    ),
    "rows-3": (
        <>
            <rect width="18" height="18" x="3" y="3" rx="2" />
            <path d="M21 9H3" />
            <path d="M21 15H3" />
        </>
    ),
    "rows-2": (
        <>
            <rect width="18" height="18" x="3" y="3" rx="2" />
            <path d="M3 12h18" />
        </>
    ),
    sun: (
        <>
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2" />
            <path d="M12 20v2" />
            <path d="m4.93 4.93 1.41 1.41" />
            <path d="m17.66 17.66 1.41 1.41" />
            <path d="M2 12h2" />
            <path d="M20 12h2" />
            <path d="m6.34 17.66-1.41 1.41" />
            <path d="m19.07 4.93-1.41 1.41" />
        </>
    ),
    moon: (
        <path d="M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6 6 0 0 0 8.268 8.268c.344-.215.825-.004.803.401" />
    ),
    "chevrons-up-down": (
        <>
            <path d="m7 15 5 5 5-5" />
            <path d="m7 9 5-5 5 5" />
        </>
    ),
    "sign-out": (
        <>
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
            <path d="m16 17 5-5-5-5" />
            <path d="M21 12H9" />
        </>
    ),
    globe: (
        <>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
            <path d="M2 12h20" />
        </>
    ),
    heading: (
        <>
            <path d="M6 12h12" />
            <path d="M6 20V4" />
            <path d="M18 20V4" />
        </>
    ),
    paragraph: (
        <>
            <path d="M13 4v16" />
            <path d="M17 4v16" />
            <path d="M19 4H9.5a4.5 4.5 0 0 0 0 9H13" />
        </>
    ),
    objective: (
        <>
            <circle cx="12" cy="12" r="10" />
            <circle cx="12" cy="12" r="6" />
            <circle cx="12" cy="12" r="2" />
        </>
    ),
    mcq: (
        <>
            <path d="M13 5h8" />
            <path d="M13 12h8" />
            <path d="M13 19h8" />
            <path d="m3 17 2 2 4-4" />
            <path d="m3 7 2 2 4-4" />
        </>
    ),
    image: (
        <>
            <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
            <circle cx="9" cy="9" r="2" />
            <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
        </>
    ),
    video: (
        <>
            <path d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" />
            <rect x="2" y="6" width="14" height="12" rx="2" />
        </>
    ),
    audio: (
        <>
            <path d="M2 10v3" />
            <path d="M6 6v11" />
            <path d="M10 3v18" />
            <path d="M14 8v7" />
            <path d="M18 5v13" />
            <path d="M22 10v3" />
        </>
    ),
    embed: (
        <>
            <path d="m10 9-3 3 3 3" />
            <path d="m14 15 3-3-3-3" />
            <rect x="3" y="3" width="18" height="18" rx="2" />
        </>
    ),
    bold: (
        <path d="M6 12h9a4 4 0 0 1 0 8H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h7a4 4 0 0 1 0 8" />
    ),
    italic: (
        <>
            <line x1="19" x2="10" y1="4" y2="4" />
            <line x1="14" x2="5" y1="20" y2="20" />
            <line x1="15" x2="9" y1="4" y2="20" />
        </>
    ),
    code: (
        <>
            <path d="m16 18 6-6-6-6" />
            <path d="m8 6-6 6 6 6" />
        </>
    ),
    link: (
        <>
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </>
    ),
    unlink: (
        <>
            <path d="m18.84 12.25 1.72-1.71h-.02a5.004 5.004 0 0 0-.12-7.07 5.006 5.006 0 0 0-6.95 0l-1.72 1.71" />
            <path d="m5.17 11.75-1.71 1.71a5.004 5.004 0 0 0 .12 7.07 5.006 5.006 0 0 0 6.95 0l1.71-1.71" />
            <line x1="8" x2="8" y1="2" y2="5" />
            <line x1="2" x2="5" y1="8" y2="8" />
            <line x1="16" x2="16" y1="19" y2="22" />
            <line x1="19" x2="22" y1="16" y2="16" />
        </>
    ),
    overflow: (
        <>
            <circle cx="12" cy="12" r="1" />
            <circle cx="19" cy="12" r="1" />
            <circle cx="5" cy="12" r="1" />
        </>
    ),
    dot: <circle cx="12" cy="12" r="4" fill="currentColor" stroke="none" />,
    info: (
        <>
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
        </>
    ),
    warning: (
        <>
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
            <path d="M12 9v4" />
            <path d="M12 17h.01" />
        </>
    ),
    danger: (
        <>
            <path d="M12 16h.01" />
            <path d="M12 8v4" />
            <path d="M15.312 2a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586l-4.688-4.688A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2z" />
        </>
    ),
    error: (
        <>
            <circle cx="12" cy="12" r="10" />
            <path d="m15 9-6 6" />
            <path d="m9 9 6 6" />
        </>
    ),
    stopped: (
        <>
            <circle cx="12" cy="12" r="10" />
            <rect x="9" y="9" width="6" height="6" rx="1" />
        </>
    ),
    // Lucide `loader-circle`.
    loading: <path d="M21 12a9 9 0 1 1-6.219-8.56" />,
    // Lucide `square`.
    stop: <rect width="18" height="18" x="3" y="3" rx="2" />,
    // Lucide `circle-alert`.
    alert: (
        <>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" x2="12" y1="8" y2="12" />
            <line x1="12" x2="12.01" y1="16" y2="16" />
        </>
    ),
    // Lucide `messages-square`.
    "conversation-long": (
        <>
            <path d="M16 10a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 14.286V4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
            <path d="M20 9a2 2 0 0 1 2 2v10.286a.71.71 0 0 1-1.212.502l-2.202-2.202A2 2 0 0 0 17.172 19H10a2 2 0 0 1-2-2v-1" />
        </>
    ),
    // Lucide `message-square-reply`.
    answered: (
        <>
            <path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z" />
            <path d="m10 8-3 3 3 3" />
            <path d="M17 14v-1a2 2 0 0 0-2-2H7" />
        </>
    ),
    // Lucide `skip-forward`.
    skipped: (
        <>
            <path d="M21 4v16" />
            <path d="M6.029 4.285A2 2 0 0 0 3 6v12a2 2 0 0 0 3.029 1.715l9.997-5.998a2 2 0 0 0 .003-3.432z" />
        </>
    ),
    // Lucide `corner-down-right`.
    "answer-line": (
        <>
            <path d="m15 10 5 5-5 5" />
            <path d="M4 4v7a4 4 0 0 0 4 4h12" />
        </>
    ),
    // Lucide `pencil-line`.
    "own-answer": (
        <>
            <path d="M13 21h8" />
            <path d="m15 5 4 4" />
            <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
        </>
    ),
    // Lucide `gauge`.
    budget: (
        <>
            <path d="m12 14 4-4" />
            <path d="M3.34 19a10 10 0 1 1 17.32 0" />
        </>
    ),
};

// An area's own glyphs: new names only (a shared name fails to type-check).
type AreaGlyphs = { readonly [K in BaseIconName]?: never } & Record<
    string,
    ReactNode
>;

type IconName =
    | BaseIconName
    | keyof typeof studioGlyphs
    | keyof typeof chatGlyphs
    | keyof typeof projectsGlyphs
    | keyof typeof libraryGlyphs
    | keyof typeof detailGlyphs;

const GLYPHS: Record<IconName, ReactNode> = {
    ...BASE_GLYPHS,
    ...studioGlyphs,
    ...chatGlyphs,
    ...projectsGlyphs,
    ...libraryGlyphs,
    ...detailGlyphs,
};

const Icon = ({ name, size = "sm", title, className }: IconProps) => {
    const decorative = title === undefined;
    return (
        <svg
            className={`bilda-icon bilda-icon--${size}${className ? ` ${className}` : ""}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            role={decorative ? undefined : "img"}
            aria-hidden={decorative ? true : undefined}
            aria-label={title}
        >
            {title !== undefined ? <title>{title}</title> : null}
            {GLYPHS[name]}
        </svg>
    );
};
